/**
 * Sihirbazın "gerçekleşen konseyi" — kaynağı ne olursa olsun tek liste.
 *
 * BULUNAN HATA (kullanıcı bildirimi): adım 4 özeti doğrudan
 * `wizard.personas` okuyordu. Kütüphane yolunda (takım / birim / tek tek
 * seçim) bu dizi KASTEN boştur — seçim `libraryTeamId` / `libraryUnitId` /
 * `libraryPersonaIds` alanlarında durur. Sonuç: "KONSEY (0)", boş liste,
 * yanlış maliyet tahmini ve — en kötüsü — `disabled={personas.length === 0}`
 * yüzünden ÇALIŞMAYAN bir "Tartışmayı Başlat" düğmesi.
 *
 * Bu hook iki kaynağı tek bir okunabilir listeye indirger. Adım 4'ün
 * `wizard.personas`'a doğrudan dokunması artık bir hatadır; hepsi buradan
 * geçmeli.
 *
 * Not: kütüphane tarafında koltuk sırası backend'de belirlenir (takım
 * `seat_index`, birim `path`+`created_at`). Buradaki sıra ÖNİZLEME içindir;
 * gerçek sıralamayı taklit etmeye çalışmak iki yerde tutarsızlık riski
 * doğururdu, o yüzden sunucunun kullandığı ölçütle aynı sırayı kullanıyoruz.
 */
import { useMemo } from "react";
import type { AgentRole } from "../domain/types";
import type { CouncilSource } from "../domain/personaLibrary";
import { usePersonaLibraryStore } from "../store/personaLibraryStore";
import { useDebateStore } from "../store/debateStore";

export interface ResolvedMember {
  id: string;
  displayName: string;
  expertise: string | null;
  role: AgentRole;
  themeColor: string;
  avatarUrl: string | null;
  /** Kalıcı kimliklerde dolu — kart üzerinde hafıza rozeti göstermek için. */
  memoryCount: number;
}

export interface ResolvedCouncil {
  members: ResolvedMember[];
  count: number;
  /** true ⇒ üyeler kütüphaneden geliyor (hafızalı kalıcı kimlikler). */
  isLibrary: boolean;
  source: CouncilSource | null;
  /** Seçilen kaynağın insan-okur adı (özet panelinde gösterilir). */
  sourceLabel: string;
  /**
   * Kütüphane kaynağı seçilmiş ama liste henüz yüklenmemişse true.
   * Bu durumda sayı 0 görünür ama bu "boş konsey" DEĞİLDİR — başlat
   * düğmesi bu ayrımı bilmek zorunda.
   */
  resolving: boolean;
}

export function useResolvedCouncil(): ResolvedCouncil {
  const wizardPersonas = useDebateStore((s) => s.wizard.personas);
  const source = useDebateStore((s) => s.wizard.councilSource);
  const teamId = useDebateStore((s) => s.wizard.libraryTeamId);
  const unitId = useDebateStore((s) => s.wizard.libraryUnitId);
  const personaIds = useDebateStore((s) => s.wizard.libraryPersonaIds);

  const personas = usePersonaLibraryStore((s) => s.personas);
  const teams = usePersonaLibraryStore((s) => s.teams);
  const units = usePersonaLibraryStore((s) => s.units);
  const libraryLoaded = usePersonaLibraryStore((s) => s.loaded);

  return useMemo<ResolvedCouncil>(() => {
    const hasLibrarySelection =
      Boolean(teamId) || Boolean(unitId) || (personaIds?.length ?? 0) > 0;

    if (!hasLibrarySelection) {
      const members = (Array.isArray(wizardPersonas) ? wizardPersonas : []).map(
        (p): ResolvedMember => ({
          id: p.id,
          displayName: p.displayName,
          expertise: p.expertise ?? null,
          role: p.role,
          themeColor: p.themeColor,
          avatarUrl: p.avatarUrl ?? null,
          memoryCount: 0,
        }),
      );
      return {
        members,
        count: members.length,
        isLibrary: false,
        source: source ?? (members.length > 0 ? "auto" : null),
        sourceLabel: "Bu soru için üretildi",
        resolving: false,
      };
    }

    const toMember = (p: {
      id: string;
      displayName: string;
      expertise: string | null;
      role: AgentRole;
      themeColor: string;
      avatarUrl: string | null;
      stats: { memoryCount: number };
    }): ResolvedMember => ({
      id: p.id,
      displayName: p.displayName,
      expertise: p.expertise,
      role: p.role,
      themeColor: p.themeColor,
      avatarUrl: p.avatarUrl,
      memoryCount: p.stats.memoryCount,
    });

    let members: ResolvedMember[] = [];
    let sourceLabel = "Kütüphaneden";

    if (teamId) {
      const team = teams.find((t) => t.id === teamId);
      members = (team?.members ?? []).map(toMember);
      sourceLabel = team ? `Takım · ${team.name}` : "Takım";
    } else if (unitId) {
      const node = units.find((u) => u.id === unitId);
      if (node) {
        // Alt ağaç dahil — backend `unit_member_ids(recursive=True)` ile
        // aynı kapsam. İkisi ayrışırsa önizleme yalan söyler.
        const scope = new Set<string>([node.id]);
        for (const u of units) {
          if ((u.path || "").startsWith(`${node.path}/`)) scope.add(u.id);
        }
        members = personas
          .filter((p) => !p.isArchived && p.unitId && scope.has(p.unitId))
          .map(toMember);
        sourceLabel = `Birim · ${node.name}`;
      }
    } else {
      const byId = new Map(personas.map((p) => [p.id, p]));
      members = (personaIds ?? [])
        .map((id) => byId.get(id))
        .filter((p): p is NonNullable<typeof p> => Boolean(p))
        .map(toMember);
      sourceLabel = "Elle seçildi";
    }

    return {
      members,
      count: members.length,
      isLibrary: true,
      source: source ?? "picked",
      sourceLabel,
      // Seçim var ama kütüphane listesi henüz gelmediyse "boş" deme.
      resolving: !libraryLoaded && members.length === 0,
    };
  }, [wizardPersonas, source, teamId, unitId, personaIds, personas, teams, units, libraryLoaded]);
}
